import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { firstValueFrom } from 'rxjs';
import { CatalogItem } from '../models/reel.model';

/**
 * Fetches the end-card marquee catalog for the Feather Reel.
 *
 * Mirrors iOS `GetAppCatalog`: a single Firestore *document* GET at `app-catalogs/{id}` (NOT a
 * query — the doc id IS the catalog id). Items with a blank image are dropped and the rest sorted
 * by `order` ascending (nulls last). Resilient like the other feature services — any error or a
 * missing doc resolves to an empty list, so the end card simply renders without a marquee.
 */
@Injectable({ providedIn: 'root' })
export class CatalogService {
  constructor(private afs: AngularFirestore, private fsContext: FirestoreContextService) {}

  async fetchCatalog(catalogId: string | null | undefined): Promise<CatalogItem[]> {
    const id = (catalogId ?? '').trim();
    if (!id) return [];

    try {
      const snapshot = await firstValueFrom(
        this.fsContext.run(() => this.afs.collection('app-catalogs').doc(id).get())
      );
      const data = snapshot?.data() as { items?: CatalogItem[] } | undefined;
      const items = data?.items ?? [];
      return items
        .filter(item => !!item?.imageUrl && item.imageUrl.trim().length > 0)
        .sort(
          (a, b) =>
            (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
        );
    } catch (error) {
      console.error('Error fetching app catalog:', error);
      return [];
    }
  }
}
